import { inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { filter, take } from "rxjs";
import { Cls_MobiDocumentViewer, Cls_MobiDynamicDialog } from "../models";
import { DynamicDialogComponent } from "../dynamic-dialog/dynamic-dialog.component";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { DynamicTableDialogComponent } from "../dynamic-table-dialog/dynamic-table-dialog.component";
import { TotalTableColumnsComponent } from "../total-table-columns/total-table-columns.component";
import { DocumentViewerComponent } from "../document-viewer/document-viewer.component";

@Injectable({
  providedIn: "root",
})
export class DialogService {
  private readonly dialog = inject(MatDialog);
  private readonly toastr = inject(ToastrService);

  constructor() {}

  matDialogDefaultConfig(config: MatDialogConfig) {
    config.hasBackdrop = true;
    config.disableClose = false;
    config.autoFocus = false;
    config.closeOnNavigation = true;
  }

  /**
   * Add CSS in Dialog Component for full screen dialog
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    mat-dialog-content {
      max-height: unset !important;
      flex: 1 0 0;
    }

   */

  matFullScreenDialogConfig(config: MatDialogConfig) {
    config.height = "calc(100% - 30px)";
    config.width = "calc(100% - 30px)";
    config.maxWidth = "100%";
    config.maxHeight = "100%";
  }

  matDialogEscepeClose(config: MatDialogRef<any, any>) {
    config
      .keydownEvents()
      .pipe(
        filter((e: KeyboardEvent) => e.code === "Escape"),
        take(1)
      )
      .subscribe(() => {
        // this.confirmationDialog()
      });
  }

  // Confirmation Dialog
  /**
   * Opens a confirmation dialog.
   * @param {string} message - The message of confirmation or note.
   * @param {string} title - The title of the dialog.
   * @param {string} onButton - The name of the confirmation button (visible only if passed).
   * @param {string} cancelButton - The name of the cancel button (visible only if passed).
   * @param {Function} [okCallback] - Optional callback function for the confirmation button.
   * @param {Function} [cancelCallback] - Optional callback function for the cancel button.
   */
  confirmationDialog(message: string, title: string, okButton: string, cancelButton: string, okCallback: () => void, cancelCallback: () => void) {
    const dialogConfig = new MatDialogConfig();
    this.matDialogDefaultConfig(dialogConfig);
    dialogConfig.width = "500px";
    dialogConfig.data = { message, title, okButton, cancelButton };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "500px",
      data: { message, title, okButton, cancelButton },
      disableClose: true,
      closeOnNavigation: true,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        okCallback();
      } else {
        cancelCallback();
      }
    });
  }

  // Dynamic Table Dialog
  /**
   * Opens a dynamic table dialog.
   * @param {Cls_MobiDynamicTableDialog} dialogConfig - Required data for the dynamic table dialog.
   * @param {Function} okCallback - Callback function to be executed when the user clicks the OK button.
   */

  /**
   * Cls_MobiDynamicTableDialog {
   *   Required - dialogHeading: The heading of the dialog.
   *   Required - responseMessage: An array of messages to be displayed in the table.
   *   Required - headerColumns: An array containing the names of the columns.
   *   Required - rowKeys: An array containing the keys of the object rows, which are passed from responseMessage.
   * }
   */
  dynamicTableDialog(dialogConfig: MatDialogConfig, okCallback: () => void) {
    this.matDialogDefaultConfig(dialogConfig);
    const dialogRef = this.dialog.open(DynamicTableDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        okCallback();
      }
    });
  }

  // Dynamic Dialog
  /**
   * Opens a dynamic dialog with customizable content and buttons.
   * @param {Cls_MobiDynamicDialog} dialogData - Modal data for the dynamic dialog.
   * @param {Function} [okCallback] - Optional callback function for the OK button.
   * @param {Function} [cancelCallback] - Optional callback function for the Cancel button.
   */

  /**
   * Cls_MobiDynamicDialog {
   *   Optional - innerContent: TemplateRef variable for content inside ng-template.
   *   Required - Title: Title of the dynamic dialog.
   *   Required - okButton: Confirmation button text (visible only if passed).
   *   Required - cancelButton: Cancel button text (visible only if passed).
   *   Optional - width: Width of the dialog (default is "99%").
   *   Optional - class: Array of classes to apply to the dialog.
   * }
   */
  dynamicDialog(dialogData: Cls_MobiDynamicDialog, okCallback: () => void, cancelCallback: () => void) {
    const dialogConfig = new MatDialogConfig();
    this.matDialogDefaultConfig(dialogConfig);
    dialogConfig.id = "dynamic-dialog";
    dialogConfig.data = dialogData;
    dialogConfig.panelClass = dialogData.class;
    dialogConfig.width = dialogData.width;
    const dialogRef = this.dialog.open(DynamicDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        okCallback();
      } else {
        cancelCallback();
      }
    });
  }

  // Table Column Setter Dialog
  tableColumnSetterDialog(selectedColumn: string[], totalColumns: any[], okCallback: (data: string[]) => void, tableName = "", UID: number) {
    const dialogRef = this.dialog.open(TotalTableColumnsComponent, {
      width: "500px",
      data: {
        selectedColumn,
        totalColumns,
        tableName,
        UID,
      },
      disableClose: false,
      closeOnNavigation: true,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        okCallback(result);
      }
    });
  }

  // Document Viewer
  /**
   * Opens the document viewer dialog.
   * @param {Cls_MobiDocumentViewer} dialogData - Required modal data for the document viewer.
   * @param {Function} [closeCallback] - Optional callback function for when the dialog is closed.
   */

  /**
   * Cls_MobiDocumentViewer {
   *   Optional - closeBtnText: Close button text (default is 'Close').
   *   Optional - closeBtnClass: Close button class for styling.
   *   Required - fileList: Array of Cls_MobiFile modal.
   *   Required - selectedFileIndex: Index of the document for full-screen view.
   * }
   */
  documentViewerDialog(dialogData: Cls_MobiDocumentViewer, closeCallback: () => void) {
    const dialogConfig = new MatDialogConfig();
    this.matDialogDefaultConfig(dialogConfig);
    this.matFullScreenDialogConfig(dialogConfig);
    dialogConfig.data = dialogData;
    const dialogRef = this.dialog.open(DocumentViewerComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(() => {
      closeCallback();
    });
  }
}
